import React, { createContext, useContext, useState } from 'react';

interface BaseAddress {
  title: string;
  details: string;
  street: string;
  roomNumber?: string;
  city: string;
  state: string;
  zipCode: string;
}

interface SenderAddress extends BaseAddress {
  senderName: string;
  senderPhone: string;
  company?: string;
  isDefault: boolean;
  id?: number;
  created_at?: string;
}

interface RecipientAddress extends BaseAddress {
  recipientName: string;
  recipientPhone: string;
  id?: number;
  created_at?: string;
}

interface LabelingProduct {
  id?: number;
  name: string;
  weight: number;
  weightUnit?: 'oz' | 'lb';
  lengthUnit?: 'cm' | 'in';
  length?: number;
  width?: number;
  height?: number;
  dangerous_goods?: boolean;
  battery_included_products?: boolean;
  categories?: string[];
  sensitivity_level?: string;
  signatureService?: string;
  insurance?: string;
  isDuplicated?: boolean;
  created_at?: string;
}

interface LabelingFormData {
  senderAddresses: SenderAddress[];
  recipientAddresses: RecipientAddress[];
  products: LabelingProduct[]; // Products array now contains signature and insurance
  selectedProducts: string[]; // Selected products tracking
}

interface LabelingFormContextType {
  formData: LabelingFormData;
  setFormData: React.Dispatch<React.SetStateAction<LabelingFormData>>;
}

const LabelingFormContext = createContext<LabelingFormContextType | undefined>(undefined);

export const LabelingFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formData, setFormData] = useState<LabelingFormData>({
    senderAddresses: [],
    recipientAddresses: [],
    products: [],
    selectedProducts: []
  });

  return (
    <LabelingFormContext.Provider value={{ formData, setFormData }}>
      {children}
    </LabelingFormContext.Provider>
  );
};

export const useLabelingFormContext = () => {
  const context = useContext(LabelingFormContext);
  if (!context) {
    throw new Error('useLabelingFormContext must be used within a LabelingFormProvider');
  }
  return context;
};

// Export types for use in other components
export type { SenderAddress, RecipientAddress, LabelingFormData, LabelingProduct };
